import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Gift, 
  Sparkles, 
  Star,
  Crown,
  Gem,
  ArrowLeft,
  RefreshCw,
  Zap,
  Trophy,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

interface MysteryBox {
  id: string;
  name: string;
  description: string;
  price: number;
  rarity: "common" | "rare" | "epic" | "legendary";
  icon: React.ReactNode;
  bgGradient: string;
  borderGlow: string;
  rewards: string[];
  odds: string;
  popular?: boolean;
  bestValue?: boolean;
}

const MysteryBoxPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [loading, setLoading] = useState<string | null>(null);
  const [showOpening, setShowOpening] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [reward, setReward] = useState<{ type: string; amount: number; rarity: string }>({ type: "", amount: 0, rarity: "" });
  const [freeBoxLoading, setFreeBoxLoading] = useState(false);
  const [freeBoxAdsWatched, setFreeBoxAdsWatched] = useState(0);

  const piBalance = user?.picoinBalance || 0;

  const mysteryBoxes: MysteryBox[] = [
    {
      id: "bronze_box",
      name: "Bronze Box",
      description: "Perfect for beginners. Small investment, guaranteed rewards!",
      price: 10,
      rarity: "common",
      icon: <Gift size={32} className="text-amber-700" />,
      bgGradient: "from-amber-800/40 to-amber-900/40",
      borderGlow: "border-amber-600/50",
      rewards: ["0.5-2 π", "50-200 ATH", "Small boosts"],
      odds: "70% π · 25% ATH · 5% Boost",
    },
    {
      id: "silver_box",
      name: "Silver Box",
      description: "Better odds, better rewards. The smart choice!",
      price: 25,
      rarity: "rare",
      icon: <Gift size={32} className="text-slate-300" />,
      bgGradient: "from-slate-500/40 to-slate-700/40",
      borderGlow: "border-slate-400/50",
      rewards: ["1-5 π", "200-1000 ATH", "Medium boosts"],
      odds: "60% π · 30% ATH · 10% Boost",
      popular: true,
    },
    {
      id: "gold_box",
      name: "Gold Box",
      description: "High rewards for serious players. Worth every Pi!",
      price: 50,
      rarity: "epic",
      icon: <Crown size={32} className="text-yellow-400" />,
      bgGradient: "from-yellow-600/40 to-amber-600/40",
      borderGlow: "border-yellow-500/50",
      rewards: ["2-10 π", "500-5000 ATH", "Premium boosts"],
      odds: "50% π · 35% ATH · 15% Boost",
    },
    {
      id: "diamond_box",
      name: "Diamond Box",
      description: "The ultimate mystery! Legendary rewards await the bold.",
      price: 100,
      rarity: "legendary",
      icon: <Gem size={32} className="text-cyan-400" />,
      bgGradient: "from-cyan-600/40 via-purple-600/40 to-pink-600/40",
      borderGlow: "border-cyan-400/50 shadow-lg shadow-cyan-500/20",
      rewards: ["5-50 π", "2000-20000 ATH", "Legendary boosts", "JACKPOT chance!"],
      odds: "40% π · 35% ATH · 20% Boost · 5% JACKPOT",
    },
    {
      id: "whale_box",
      name: "Whale Box",
      description: "The ultimate gamble! Massive rewards for the boldest players. Win up to 200π!",
      price: 500,
      rarity: "legendary",
      icon: <Crown size={32} className="text-amber-400" />,
      bgGradient: "from-amber-500/40 via-yellow-500/40 to-orange-500/40",
      borderGlow: "border-amber-400/60 shadow-xl shadow-amber-500/30",
      rewards: ["50-200 π", "50K-200K ATH", "Lifetime Pro", "MEGA JACKPOT!"],
      odds: "35% π · 30% ATH · 25% Boost · 10% JACKPOT",
      bestValue: true,
    },
  ];

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case "common": return "text-amber-600";
      case "rare": return "text-slate-300";
      case "epic": return "text-yellow-400";
      case "legendary": return "text-cyan-400";
      default: return "text-white";
    }
  };

  const handleFreeBoxAd = async () => {
    if (freeBoxLoading) return;
    setFreeBoxLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user?.id || '', () => {
        const newCount = freeBoxAdsWatched + 1;
        setFreeBoxAdsWatched(newCount);
        if (newCount >= 3) {
          // Open free bronze box
          handleOpenBox({ id: 'bronze_box', name: 'Bronze Box', price: 0, rarity: 'common' } as any);
          setFreeBoxAdsWatched(0);
        }
      });
    } catch (error) {
      console.error('Free box ad error:', error);
    } finally {
      setFreeBoxLoading(false);
    }
  };

  const handleOpenBox = async (box: MysteryBox) => {
    if (loading) return;
    
    if (piBalance < box.price) {
      alert(`Not enough Pi! You need ${box.price} π but only have ${piBalance.toFixed(2)} π`);
      return;
    }

    setLoading(box.id);
    setShowOpening(true);

    try {
      // Call backend to open mystery box
      const result = await arthereumProvider.openMysteryBox(user!.id, box.id, box.price);
      
      // Update Redux locally instead of re-fetching from Firestore
      const athReward = result.type === "ath" ? result.amount : 0;
      const piReward = result.type === "pi" ? result.amount : 0;
      dispatch(actions.setActualUser({
        ...userData,
        user: {
          ...user,
          picoinBalance: (user!.picoinBalance || 0) - box.price + piReward,
          arthereumBalance: (user!.arthereumBalance || 0) + athReward,
        },
        miningInfos: userData.miningInfos,
      }));
      
      setReward({
        type: result.type,
        amount: result.amount,
        rarity: result.rarity,
      });

      // Show opening animation then reveal
      setTimeout(() => {
        setShowOpening(false);
        setShowResult(true);
      }, 2000);
    } catch (error: any) {
      setShowOpening(false);
      alert(error.message || "Failed to open box. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      <CommonPageTitle title="Mystery Box" padding={null} />
      
      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className={`p-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}
          >
            <ArrowLeft size={20} className={theme === "dark" ? "text-white" : "text-gray-900"} />
          </button>
          <div>
            <h1 className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              Mystery Box 🎁
            </h1>
            <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
              Unlock surprises with every box!
            </p>
          </div>
        </div>

        {/* Balance Card */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`
            p-4 rounded-2xl mb-6 flex items-center justify-between
            ${theme === "dark" ? "bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/20" : "bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300"}
          `}
        >
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider ${theme === "dark" ? "text-purple-400" : "text-purple-600"}`}>
              Your Pi Balance
            </p>
            <p className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {piBalance.toFixed(2)} π
            </p>
          </div>
          <button 
            onClick={() => navigate(PAGE_NAME.DEPOSIT)}
            className="px-4 py-2 rounded-xl bg-purple-500 text-white font-bold text-sm hover:bg-purple-600 transition-colors"
          >
            + Add Pi
          </button>
        </motion.div>

        {/* Info Banner */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`
            p-3 rounded-xl mb-6 flex items-center gap-3
            bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border border-emerald-500/30
          `}
        >
          <Trophy size={20} className="text-emerald-400 shrink-0" />
          <p className={`text-xs ${theme === "dark" ? "text-emerald-300" : "text-emerald-600"}`}>
            <strong>💰 Guaranteed rewards!</strong> Every box contains valuable prizes. No empty boxes!
          </p>
        </motion.div>

        {/* Free Box via Ads */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`p-4 rounded-2xl mb-6 border-2 border-dashed ${
            theme === "dark" ? "bg-emerald-900/20 border-emerald-500/30" : "bg-emerald-50 border-emerald-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${theme === "dark" ? "bg-emerald-500/20" : "bg-emerald-100"}`}>
                <Gift size={24} className="text-emerald-500" />
              </div>
              <div>
                <p className={`font-bold text-sm ${theme === "dark" ? "text-emerald-300" : "text-emerald-800"}`}>
                  🎬 Free Bronze Box!
                </p>
                <p className={`text-[10px] ${theme === "dark" ? "text-emerald-400/60" : "text-emerald-600"}`}>
                  Watch 3 ads to open for FREE
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`text-xs font-black ${theme === "dark" ? "text-emerald-300" : "text-emerald-700"}`}>
                {freeBoxAdsWatched}/3 ads
              </span>
              <button
                onClick={handleFreeBoxAd}
                disabled={freeBoxLoading}
                className="px-4 py-2 rounded-xl bg-emerald-500 text-white font-bold text-xs hover:bg-emerald-600 active:scale-95 transition-all"
              >
                {freeBoxLoading ? '...' : freeBoxAdsWatched >= 2 ? '🎁 Last Ad!' : '▶ Watch Ad'}
              </button>
            </div>
          </div>
          {/* Progress bar */}
          <div className={`mt-3 h-2 rounded-full overflow-hidden ${theme === "dark" ? "bg-emerald-900/50" : "bg-emerald-200"}`}>
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(freeBoxAdsWatched / 3) * 100}%` }} />
          </div>
        </motion.div>

        {/* Mystery Boxes */}
        <div className="space-y-4">
          {mysteryBoxes.map((box, idx) => (
            <motion.div
              key={box.id}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              className={`
                relative overflow-hidden rounded-2xl p-5
                bg-gradient-to-r ${box.bgGradient}
                border-2 ${box.borderGlow}
                ${theme === "dark" ? "bg-slate-800/80" : "bg-white/90"}
              `}
            >
              {/* Badges */}
              <div className="absolute top-3 right-3 flex gap-2">
                {box.popular && (
                  <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                    ⭐ POPULAR
                  </span>
                )}
                {box.bestValue && (
                  <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-gradient-to-r from-cyan-400 to-pink-500 text-white animate-pulse">
                    💎 BEST VALUE
                  </span>
                )}
              </div>

              <div className="flex items-start gap-4">
                <motion.div 
                  className={`p-4 rounded-xl shrink-0 ${theme === "dark" ? "bg-slate-700/50" : "bg-white/50"}`}
                  whileHover={{ rotate: [0, -10, 10, -10, 0], transition: { duration: 0.5 } }}
                >
                  {box.icon}
                </motion.div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-bold text-lg ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                      {box.name}
                    </h3>
                    <span className={`text-[10px] font-bold uppercase ${getRarityColor(box.rarity)}`}>
                      {box.rarity}
                    </span>
                  </div>
                  <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"} mb-3`}>
                    {box.description}
                  </p>
                  
                  {/* Possible Rewards */}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {box.rewards.map((r, i) => (
                      <span
                        key={i}
                        className={`text-[10px] px-2 py-0.5 rounded-full ${theme === "dark" ? "bg-white/10 text-white/80" : "bg-gray-100 text-gray-600"}`}
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                  
                  <p className={`text-[10px] ${theme === "dark" ? "text-slate-500" : "text-gray-400"} mb-3`}>
                    Odds: {box.odds}
                  </p>

                  {/* Open Button */}
                  <button
                    onClick={() => handleOpenBox(box)}
                    disabled={loading === box.id || piBalance < box.price}
                    className={`
                      w-full py-3 rounded-xl font-bold text-sm transition-all transform active:scale-95 flex items-center justify-center gap-2
                      ${piBalance < box.price 
                        ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                        : `bg-gradient-to-r ${box.rarity === 'legendary' ? 'from-cyan-500 via-purple-500 to-pink-500' : 'from-purple-500 to-pink-500'} text-white hover:shadow-lg hover:shadow-purple-500/30`
                      }
                    `}
                  >
                    {loading === box.id ? (
                      <RefreshCw className="animate-spin" size={18} />
                    ) : piBalance < box.price ? (
                      <>Need {box.price} π</>
                    ) : (
                      <>
                        <Gift size={16} />
                        Open for {box.price} π
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Recent Winners */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className={`
            mt-6 p-4 rounded-2xl
            ${theme === "dark" ? "bg-slate-800/50 border border-slate-700" : "bg-white border border-gray-200"}
          `}
        >
          <h4 className={`text-sm font-bold mb-3 flex items-center gap-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
            <Zap size={16} className="text-yellow-400" /> Recent Winners
          </h4>
          <div className="space-y-2">
            {[
              { user: "cryptoKing***", reward: "15.5 π", box: "Diamond", time: "2m ago" },
              { user: "lucky777***", reward: "5,000 ATH", box: "Gold", time: "5m ago" },
              { user: "pioneer***", reward: "Flash Boost", box: "Silver", time: "8m ago" },
            ].map((winner, i) => (
              <div key={i} className={`flex items-center justify-between text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
                <span>{winner.user}</span>
                <span className="font-bold text-emerald-400">{winner.reward}</span>
                <span className="text-[10px]">{winner.time}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Opening Animation */}
      <AnimatePresence>
        {showOpening && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          >
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 10, -10, 0],
                scale: [1, 1.1, 1, 1.1, 1]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-center"
            >
              <Gift size={100} className="text-purple-400 mx-auto mb-4" />
              <p className="text-white text-xl font-bold">Opening...</p>
              <p className="text-purple-300 text-sm">Your reward is being revealed!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result Modal */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowResult(false)}
          >
            <motion.div
              initial={{ scale: 0.5, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              className={`
                w-full max-w-sm rounded-[40px] p-8 text-center space-y-6 shadow-2xl
                ${reward.rarity === 'legendary' ? 'bg-gradient-to-br from-cyan-600 via-purple-600 to-pink-600' : 
                  reward.rarity === 'epic' ? 'bg-gradient-to-br from-yellow-600 to-amber-600' :
                  reward.rarity === 'rare' ? 'bg-gradient-to-br from-slate-500 to-slate-700' :
                  'bg-gradient-to-br from-purple-600 to-pink-600'}
              `}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ delay: 0.2 }}
                className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto shadow-xl"
              >
                <Sparkles size={48} className="text-yellow-500" />
              </motion.div>
              <div className="space-y-2">
                <h2 className="text-3xl font-black text-white">You Won!</h2>
                <p className={`text-sm uppercase font-bold ${getRarityColor(reward.rarity)}`}>
                  {reward.rarity} Reward
                </p>
              </div>
              <div className="bg-white/20 rounded-2xl p-4">
                <p className="text-4xl font-black text-white">
                  {reward.amount} {reward.type === 'pi' ? 'π' : reward.type === 'ath' ? 'ATH' : ''}
                </p>
                <p className="text-white/80 text-sm mt-1">
                  {reward.type === 'pi' ? 'Pi coins added to your wallet!' : 
                   reward.type === 'ath' ? 'ATH tokens credited!' : 
                   'Boost activated!'}
                </p>
              </div>
              <button
                onClick={() => setShowResult(false)}
                className="w-full py-4 bg-white text-purple-700 rounded-2xl font-bold hover:bg-slate-100"
              >
                Open Another Box! 🎁
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MysteryBoxPage;
